import * as React from 'react'
import * as classNames from 'classnames'
import {
  addDays,
  getYear,
  getMonth,
  getDay,
  getWeeksInMonth,
  lastDayOfMonth,
  addMonths,
  subMonths,
  eachYearOfInterval,
} from 'date-fns'
import ja from 'date-fns/locale/ja'
import { useForm, UseFormRegister } from 'react-hook-form'
import DatePicker from 'react-datepicker'
import { motion, AnimatePresence } from 'framer-motion'

import { TimePicker } from '~/atoms/search/TimePicker'

import CheckIcon from './icon_check.svg?reactCompornt'
import ChevronLeftIcon from './icon_chevron_left.svg?reactCompornt'

import * as Style from './style.css'
import 'react-datepicker/dist/react-datepicker.css'
import './datepicker.css'

const MOBILE_ANIMATION_SETTING = {
  initial: { y: '100%', opacity: 0 },
  animate: { y: '0%', opacity: 1 },
  exit: { y: '100%', opacity: 0 },
  transition: { duration: 0.5 },
}

// アニメーションなしでも表示のイベントをハンドリングできるように
// 0秒の意味のないイベントを設定
const NO_ANIMATION_SETTING = {
  animate: { z: 0 },
  exit: { z: 0 },
  transition: { duration: 0 },
}

export interface IProps {
  prevHandler: () => void
  register?: UseFormRegister<ISearchConditionState>
  submitHandler: () => void
  resetDateHandler: () => void
  resetTimeHandler: () => void
  selectedDate?: Date
  minDate?: Date
  maxDate?: Date
  workTimeFrom: string
  workTimeTo: string
  workTimeFromUnselected: number
  workTimeToUnselected: number
  setWorkTimeFromHandler: (timeString: string) => void
  setWorkTimeToHandler: (timeString: string) => void
  setWorkTimeFromUnselectedHandler: (value: number) => void
  setWorkTimeToUnselectedHandler: (value: number) => void

  setDateHandler: (date: Date) => void
  setTodayHandler: (date: Date) => void
  setTomorrowHandler: (date: Date) => void
}

export const SearchConditionDate = ({
  prevHandler,
  register,
  submitHandler,
  resetDateHandler,
  resetTimeHandler,
  selectedDate,
  setTodayHandler,
  setTomorrowHandler,
  workTimeFrom,
  workTimeTo,
  workTimeFromUnselected,
  workTimeToUnselected,
  setWorkTimeFromHandler,
  setWorkTimeToHandler,
  setWorkTimeFromUnselectedHandler,
  setWorkTimeToUnselectedHandler,
  minDate,
  maxDate,
  setDateHandler,
}: IProps): JSX.Element => {
  const { watch } = useForm<ISearchConditionState>()
  const today = new Date()
  const tomorrow = addDays(new Date(), 1)
  const years: number[] = eachYearOfInterval({
    start: minDate,
    end: maxDate,
  }).map((date) => date.getFullYear())

  const MONTHS: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  const [isYearEdit, setYearEdit] = React.useState(false)
  const [isMonthEdit, setMonthEdit] = React.useState(false)
  const [forceUpdateCount, setForceUpdateCount] = React.useState(0)
  const [isForceUpdateCountUp, setIsForceUpdateCountUp] = React.useState(false)
  const [currentCalendarDate, setCurrentCalendarDate] = React.useState<Date>(
    typeof selectedDate !== 'undefined' ? selectedDate : minDate,
  )
  const setDateAfterEventHandler = (date: Date) => {
    setDateHandler(date)
    setCurrentCalendarDate(date)
  }
  const forceUpdateCountUp = () => {
    const nextCount = forceUpdateCount
    setForceUpdateCount(nextCount + 1)
  }

  const isNextMonthRowUpCss = getDay(lastDayOfMonth(currentCalendarDate)) !== 6
  const isNextMonth2weekHideCss =
    getWeeksInMonth(lastDayOfMonth(currentCalendarDate)) === 6 ||
    (getWeeksInMonth(lastDayOfMonth(currentCalendarDate)) === 5 &&
      getDay(lastDayOfMonth(currentCalendarDate)) === 6)
  const currentCalendarDateHandler = (date: Date) => {
    setCurrentCalendarDate(date)
  }

  /*-------------- iOS16のレンダリング漏れ対策 --------------*/
  React.useEffect(() => {
    setIsForceUpdateCountUp(true)
    const timeoutId = setTimeout(() => {
      setIsForceUpdateCountUp(false)
    }, 200)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [forceUpdateCount])

  return (
    <>
      <div className={Style.SearchConditionDate}>
        <div className={Style.SearchConditionHeaderWrapper}>
          <div className={Style.SearchConditionDateHeader}>
            単発予約
            <ChevronLeftIcon
              className={Style.SearchConditionHeaderPrevIcon}
              onClick={prevHandler}
            />
            <div
              className={Style.SearchConditionHeaderPrevName}
              onClick={prevHandler}
            ></div>
          </div>
        </div>
        <div>
          <div className={Style.SearchDateHeader}>日付指定</div>
          <div className={Style.SearchDate}>
            <div
              className={classNames({
                [Style.SearchDatePicker]: true,
                [Style.NextMonthRowUpCss]: isNextMonthRowUpCss,
                [Style.NextMonth2weekhideCss]: isNextMonth2weekHideCss,
                [Style.ForceUpdateCss]: isForceUpdateCountUp,
              })}
            >
              <DatePicker
                onChange={() => void 0}
                selected={selectedDate}
                onSelect={(date: Date) => {
                  setDateAfterEventHandler(date)
                  forceUpdateCountUp()
                }}
                locale={ja}
                minDate={minDate}
                maxDate={maxDate}
                inline
                monthsShown={2}
                focusSelectedMonth={true}
                renderCustomHeader={({
                  date,
                  changeYear,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div>
                    <div className={Style.SearchDateSelecter}>
                      <span
                        className={Style.SearchDatePickerSetDate}
                        onClick={() => {
                          changeYear(getYear(today))
                          changeMonth(getMonth(today))
                          setTodayHandler(today)
                          currentCalendarDateHandler(today)
                          forceUpdateCountUp()
                        }}
                      >
                        今日
                      </span>
                      <span
                        className={Style.SearchDatePickerSetDate}
                        onClick={() => {
                          changeYear(getYear(tomorrow))
                          changeMonth(getMonth(tomorrow))
                          setTomorrowHandler(tomorrow)
                          currentCalendarDateHandler(tomorrow)
                          forceUpdateCountUp()
                        }}
                      >
                        明日
                      </span>
                    </div>
                    <div className={Style.reactDatepickerNavigation}>
                      <button
                        type="button"
                        className={Style.reactDatepickerNavigationPrev}
                        aria-label="Previous Month"
                        onClick={() => {
                          decreaseMonth()
                          currentCalendarDateHandler(subMonths(date, 1))
                          forceUpdateCountUp()
                        }}
                        style={
                          prevMonthButtonDisabled
                            ? { visibility: 'hidden' }
                            : null
                        }
                      >
                        <span className={Style.SearchDatePrev}>前月</span>
                      </button>
                      <button
                        type="button"
                        className={Style.reactDatepickerNavigationNext}
                        aria-label="Next Month"
                        onClick={() => {
                          increaseMonth()
                          currentCalendarDateHandler(addMonths(date, 1))
                          forceUpdateCountUp()
                        }}
                        style={
                          nextMonthButtonDisabled
                            ? { visibility: 'hidden' }
                            : null
                        }
                      >
                        <span className={Style.SearchDateNext}>次月</span>
                      </button>
                      <div className={Style.SearchDateDropdownYear}>
                        <span
                          className={Style.SearchDateDropdownYearLabel}
                          onClick={() => {
                            setYearEdit(true)
                            forceUpdateCountUp()
                          }}
                        >
                          {getYear(date)}年
                        </span>
                        <div
                          className={
                            isYearEdit
                              ? Style.DateDropdownWrapperYearEdit
                              : Style.DateDropdownWrapper
                          }
                        >
                          {years.map((year, index) => (
                            <span
                              className={
                                year === getYear(date)
                                  ? Style.SearchDateDropdownSelected
                                  : Style.SearchDateDropdown
                              }
                              onClick={() => {
                                changeYear(year)
                                setYearEdit(false)
                                date.setFullYear(year)
                                currentCalendarDateHandler(date)
                                forceUpdateCountUp()
                              }}
                              key={`dropdown-year-index-${index}`}
                            >
                              {year}年
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className={Style.SearchDateDropdownMonth}>
                        <span
                          className={Style.SearchDateDropdownMonthLabel}
                          onClick={() => setMonthEdit(true)}
                        >
                          {getMonth(date) + 1}月
                        </span>
                        <div
                          className={
                            isMonthEdit
                              ? Style.DateDropdownWrapperMonthEdit
                              : Style.DateDropdownWrapper
                          }
                        >
                          {MONTHS.map((month: number, index) => (
                            <span
                              className={
                                month === getMonth(date) + 1
                                  ? Style.SearchDateDropdownSelected
                                  : Style.SearchDateDropdown
                              }
                              onClick={() => {
                                changeMonth(MONTHS.indexOf(month))
                                setMonthEdit(false)
                                date.setMonth(MONTHS.indexOf(month))
                                currentCalendarDateHandler(date)
                                forceUpdateCountUp()
                              }}
                              key={`dropdown-month-index-${index}`}
                            >
                              {month}月
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
              <div className={Style.SearchResetTime} onClick={resetDateHandler}>
                日付指定をリセット
              </div>
            </div>
          </div>
        </div>
        <div className={Style.SearchConditionDateTimeHeader}>時間指定</div>
        <div className={Style.SearchConditionDateTimeWrapper}>
          <ul className={Style.SearchTimeInput}>
            <li className={Style.SearchTimeSelecter}>
              <label className={Style.SearchTimeSelecterLabel}>開始</label>
              <TimePicker
                label={!workTimeFrom ? '指定しない' : workTimeFrom}
                defaultHour={(workTimeFrom || '00').slice(0, 2)}
                defaultMinute={(workTimeFrom || '00').slice(3, 5)}
                setTimeHandler={setWorkTimeFromHandler}
              />
              <div className={Style.SearchConditionFilterDay}>
                <input
                  type="checkbox"
                  className={Style.SearchConditionFilterDayCtrl}
                  {...register('search.work_time_from_unselected')}
                  id="workTimeFromUnselected"
                  checked={workTimeFromUnselected === 1}
                  onChange={(e) =>
                    setWorkTimeFromUnselectedHandler(e.target.checked ? 1 : 0)
                  }
                  value={1}
                />
                <label
                  htmlFor="workTimeFromUnselected"
                  className={Style.SearchConditionFilterDayLabel}
                >
                  <span className={Style.SearchConditionFilterDayCheck}>
                    <CheckIcon
                      className={Style.SearchConditionFilterDayCheckIcon}
                    />
                  </span>
                  指定無し
                </label>
              </div>
            </li>
            <li className={Style.SearchTimeBetweenLabel}>～</li>
            <li className={Style.SearchTimeSelecter}>
              <label className={Style.SearchTimeSelecterLabel}>終了</label>
              <TimePicker
                label={!workTimeTo ? '指定しない' : workTimeTo}
                defaultHour={(workTimeTo || '00').slice(0, 2)}
                defaultMinute={(workTimeTo || '00').slice(3, 5)}
                setTimeHandler={setWorkTimeToHandler}
              />
              <div className={Style.SearchConditionFilterDay}>
                <input
                  type="checkbox"
                  className={Style.SearchConditionFilterDayCtrl}
                  {...register('search.work_time_to_unselected')}
                  id="workTimeToUnselected"
                  checked={workTimeToUnselected === 1}
                  onChange={(e) =>
                    setWorkTimeToUnselectedHandler(e.target.checked ? 1 : 0)
                  }
                  value={1}
                />
                <label
                  htmlFor="workTimeToUnselected"
                  className={Style.SearchConditionFilterDayLabel}
                >
                  <span className={Style.SearchConditionFilterDayCheck}>
                    <CheckIcon
                      className={Style.SearchConditionFilterDayCheckIcon}
                    />
                  </span>
                  指定無し
                </label>
              </div>
            </li>
          </ul>
          {workTimeFrom >= workTimeTo &&
            workTimeFromUnselected === 0 &&
            workTimeToUnselected === 0 && (
              <p className={Style.DateErrorMessage}>
                開始時間と終了時間を正しく指定してください
              </p>
            )}
          <div className={Style.SearchResetTime} onClick={resetTimeHandler}>
            時間指定をリセット
          </div>
        </div>
        <AnimatePresence>
          <motion.div
            className={Style.FixedSearchButtonContainer}
            {...NO_ANIMATION_SETTING}
          >
            <span className={Style.SearchButton} onClick={submitHandler}>
              決定
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  )
}

export default SearchConditionDate
